import React from 'react';
import { Phone, MessageCircle } from 'lucide-react';
import { PHONES, CONTACT } from '../content';
import { onCallClick, onWhatsappClick } from '../lib/trackHelpers';

const WA_ICON = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

export default function CTAStrip() {
  return (
    <section
      data-testid="cta-strip"
      style={{
        background: 'linear-gradient(135deg, rgba(229,51,51,.08) 0%, rgba(5,5,13,1) 100%)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        padding: '5rem 0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Radial glow */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '20%',
          transform: 'translateY(-50%)',
          width: 700,
          height: 500,
          background: 'radial-gradient(ellipse, rgba(229,51,51,.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Grid pattern overlay (subtle) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          opacity: 0.03,
          pointerEvents: 'none',
        }}
      />

      <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '4rem',
            alignItems: 'center',
          }}
          className="cta-grid"
        >
          {/* Left */}
          <div>
            {/* Live indicator */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '.75rem',
                marginBottom: '1.25rem',
              }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  background: 'var(--green)',
                }}
                className="live"
              />
              <span
                style={{
                  fontSize: '.8rem',
                  color: 'var(--green)',
                  fontWeight: 700,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                Şu An Aktif — 7/24 Hizmet
              </span>
            </div>

            {/* Headline */}
            <h2
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                color: 'var(--text)',
                lineHeight: 1.08,
                letterSpacing: '-.035em',
                marginBottom: '1rem',
              }}
            >
              Yolda Kalma.
              <br />
              <span style={{ color: 'var(--red)' }}>Hemen Ara.</span>
            </h2>

            <p className="body-lg" style={{ maxWidth: 480, marginBottom: '.75rem' }}>
              Kocaeli genelinde ortalama <strong style={{ color: 'var(--text)' }}>30 dakikada</strong> yanınızdayız.
              Servis saati beklemeyin. Tek bir telefon yeterli.
            </p>

            {/* Urgency microcopy */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '.4rem',
              marginBottom: '1.25rem',
              fontSize: '.78rem', color: 'var(--green)', fontWeight: 600,
            }}>
              <span className="live" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              Konumunuzu gönderin, hemen çıkalım
            </div>

            {/* Trust points */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '.625rem',
              }}
            >
              {['12+ ilçe kapsama', '24 ay garanti', 'Sertifikalı ekip'].map((t) => (
                <div
                  key={t}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '.4rem',
                    background: 'rgba(13,14,32,.6)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border)',
                    borderRadius: 100,
                    padding: '.375rem .875rem',
                    fontSize: '.8rem',
                    color: 'var(--text2)',
                  }}
                >
                  <span style={{ fontSize: '.72rem' }}>✓</span> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Right — CTA Buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '.875rem',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--rxl)',
              padding: '2rem',
            }}
          >
            <div style={{ marginBottom: '.5rem' }}>
              <div
                style={{
                  fontSize: '.72rem',
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text2)',
                  marginBottom: '.5rem',
                }}
              >
                Hemen İletişime Geçin
              </div>
              <div style={{ fontSize: '.85rem', color: 'var(--text2)' }}>
                3 aktif hat ile kesintisiz hizmet
              </div>
            </div>

            <a
              href={PHONES.main_tel}
              data-testid="cta-call-button"
              className="btn btn-primary btn-xl num ring"
              onClick={onCallClick('/')}
              style={{ justifyContent: 'center', gap: '.75rem' }}
            >
              <Phone size={18} /> {PHONES.main}
            </a>

            <a
              href={PHONES.secondary_tel}
              className="btn btn-secondary btn-lg num"
              style={{ justifyContent: 'center', gap: '.625rem' }}
            >
              <Phone size={16} /> {PHONES.secondary}
            </a>

            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              data-gtm-action="whatsapp_click"
              className="btn btn-green"
              onClick={onWhatsappClick('/')}
              style={{ justifyContent: 'center', gap: '.625rem' }}
            >
              {WA_ICON} WhatsApp Destek
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @media(max-width: 768px) {
          .cta-grid {
            grid-template-columns: 1fr !important;
            gap: 2.5rem !important;
          }
        }
      `}</style>
    </section>
  );
}
